import { Image, StyleSheet, Text, View } from '@react-pdf/renderer';

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import PrintTable from 'pages/Settings/Print/components/common/PrintTable';
import usePrintHelper from 'pages/Settings/Print/hooks/useHelpers';

import { pdfScales } from './MainTemplate';
import RTLTextForPDF from './RTLTextForPDF';
import { itemConfig } from './Template1';

// Template 5: Government Invoice Template
/**
 *
 * @author @omerrkosar
 */

const Template5 = ({
	orderID,
	orderDate,
	grandTotal,
	subTotal,
	totalDue,
	discountAmount,
	codFee,
	taxAmount,
	shippingAmount,
	shippingDiscountAmount,
	VATNumber,
	CRNumber,
	paymentMethod,
	receiverName,
	receiverAddress,
	receiverCity,
	receiverCountry,
	currency,
	receiverTelephone,
	senderName,
	senderAddress,
	invoiceAddress,
	items = [],
	shipmentNumber,
	shippingCompanyName,
	dateOfShipmentArrival,
	shippingDescription,
	orderComment = null,
	// companyLogo = null,
	// brandLogo = null,
	qrCode = null,
	couponCode = null,
	propJSON,
	ref2
}) => {
	const {
		showOrderItems = true,
		showQrCode = true,
		showVatNumber = true,
		showCRNumber = true,
		showOrderNumber = true,
		// showBarcode = true,
		showOrderDate = true,
		showInvoiceCreatedDate = true,
		showGrandTotal = true,
		showTotalDue = true,
		showPaymentMethod = true,
		showPhoneNumber = true,
		showFullName = true,
		showSenderName = true,
		showSenderAddress = true,
		showAddress = true,
		showCity = false,
		showCountry = false,
		showVatAmount = true,
		showShipFee = true,
		showShippingDiscount = false,
		showDiscount = true,
		showCodFee = false,
		showSubTotal = true,
		showTotalQuantity = true,
		showShipmentNumber = true,
		showDeliveryCompany = true,
		showDateOfShipmentArrival = true,
		showShippingDescription = true,
		showOrderComment = false,
		showCouponCode = false,
		// logoType = 'companyLogo',
		pageSize = 'A4',
		fontSize = '10px',
		// barcodeSize = 'default',
		// orderNumberBarcodeFormat = 'CODE128',
		language,
		title,
		columnSettings = [],
		showRef2 = false
	} = propJSON;

	const { t } = useTranslation('translation', {
		lng: language === 'arabicValue' ? 'engValue' : language
	});
	const { getTemplateFontSize } = usePrintHelper();

	const pageScale = pdfScales[pageSize];
	const calculatedFontSize = getTemplateFontSize(fontSize, pageScale);

	const totalQuantity = useMemo(() => items?.reduce((acc, item) => acc + item.quantity, 0), [items]);

	const styles = useMemo(
		() =>
			StyleSheet.create({
				section: {
					padding: 2,
					display: 'flex',
					flexDirection: 'column',
					justifyContent: 'center',
					alignItems: 'center',
					borderBottomWidth: 0.5,
					borderTopWidth: 0,
					borderLeftWidth: 0,
					borderRightWidth: 0
				},
				table: {
					width: 'auto',
					marginTop: (pageSize !== 'A7' || pageSize !== 'A6' ? 15 : 0) * pageScale,
					marginBottom: (pageSize !== 'A7' || pageSize !== 'A6' ? 30 : 0) * pageScale
				},
				tableRow: {
					width: '100%',
					display: 'flex',
					height: 'auto',
					flexDirection: 'row'
				},
				textBlock: {
					paddingVertical: 3
				},
				tableCol: {
					padding: (pageSize !== 'A7' || pageSize !== 'A6' ? 10 : 0) * pageScale * pageScale
				},
				tableCell: {
					marginTop: 0,
					textAlign: 'center'
				},
				header: { fontSize: 1.6 * calculatedFontSize },
				qrCode: {
					width: 70 * pageScale,
					height: 70 * pageScale,
					objectFit: 'cover'
				}
			}),
		[pageScale, pageSize, calculatedFontSize]
	);

	return (
		<>
			<View>
				{/** header */}
				<View style={[styles.section, styles.header]}>
					{!title ? (
						<>
							<Text style={[styles.tableRow, { textAlign: 'center' }]}>
								{t('simplifiedTaxInvoice', { lng: 'arabicValue' })}
							</Text>
							<Text style={[styles.tableRow, { textAlign: 'center' }]}>{t('simplifiedTaxInvoice')}</Text>
						</>
					) : (
						<Text style={[styles.tableRow, { textAlign: 'center' }]}>{title}</Text>
					)}
				</View>

				{/** first section */}
				<View style={[styles.table]}>
					{showOrderNumber && (
						<View style={[styles.tableRow, styles.textBlock]}>
							<View style={[{ width: '50%' }]}>
								<Text style={[styles.tableCell, { textAlign: 'left' }]}>
									{t('orderNo')} : {orderID}
								</Text>
							</View>
							<View style={[{ width: '50%' }]}>
								<Text style={[styles.tableCell, { textAlign: 'right' }]}>
									{orderID} : {t('orderNo', { lng: 'arabicValue' })}
								</Text>
							</View>
						</View>
					)}
					{showOrderDate && (
						<View style={[styles.tableRow, styles.textBlock]}>
							<View style={[{ width: '50%' }]}>
								<Text style={[styles.tableCell, { textAlign: 'left' }]}>
									{t('orderDate')} : {orderDate}
								</Text>
							</View>
							<View style={[{ width: '50%' }]}>
								<Text style={[styles.tableCell, { textAlign: 'right' }]}>
									{orderDate} : {t('orderDate', { lng: 'arabicValue' })}
								</Text>
							</View>
						</View>
					)}
					{showInvoiceCreatedDate && (
						<View style={[styles.tableRow, styles.textBlock]}>
							<View style={[{ width: '50%' }]}>
								<Text style={[styles.tableCell, { textAlign: 'left' }]}>
									{t('invoiceCreatedDate')} : {orderDate}
								</Text>
							</View>
							<View style={[{ width: '50%' }]}>
								<Text style={[styles.tableCell, { textAlign: 'right' }]}>
									{orderDate} : {t('invoiceCreatedDate', { lng: 'arabicValue' })}
								</Text>
							</View>
						</View>
					)}
					{showDateOfShipmentArrival && (
						<View style={[styles.tableRow, styles.textBlock]}>
							<View style={[{ width: '50%' }]}>
								<Text style={[styles.tableCell, { textAlign: 'left' }]}>
									{t('dateOfShipmentArrival')} : {dateOfShipmentArrival}
								</Text>
							</View>
							<View style={[{ width: '50%' }]}>
								<Text style={[styles.tableCell, { textAlign: 'right' }]}>
									{dateOfShipmentArrival} : {t('dateOfShipmentArrival', { lng: 'arabicValue' })}
								</Text>
							</View>
						</View>
					)}
					{showDeliveryCompany && (
						<View style={[styles.tableRow, styles.textBlock]}>
							<View style={[{ width: '50%' }]}>
								<Text style={[styles.tableCell, { textAlign: 'left' }]}>
									{t('deliveryCompany')} : {shippingCompanyName}
								</Text>
							</View>
							<View style={[{ width: '50%' }]}>
								<Text style={[styles.tableCell, { textAlign: 'right' }]}>
									{shippingCompanyName} : {t('deliveryCompany', { lng: 'arabicValue' })}
								</Text>
							</View>
						</View>
					)}
					{showShipmentNumber && (
						<View style={[styles.tableRow, styles.textBlock]}>
							<View style={[{ width: '50%' }]}>
								<Text style={[styles.tableCell, { textAlign: 'left' }]}>
									{t('shipmentNumber')} : {shipmentNumber}
								</Text>
							</View>
							<View style={[{ width: '50%' }]}>
								<Text style={[styles.tableCell, { textAlign: 'right' }]}>
									{shipmentNumber} : {t('shipmentNumber', { lng: 'arabicValue' })}
								</Text>
							</View>
						</View>
					)}
					{showVatNumber && (
						<View style={[styles.tableRow, styles.textBlock]}>
							<View style={[{ width: '50%' }]}>
								<Text style={[styles.tableCell, { textAlign: 'left' }]}>
									{t('vatNumber')} : {VATNumber}
								</Text>
							</View>
							<View style={[{ width: '50%' }]}>
								<Text style={[styles.tableCell, { textAlign: 'right' }]}>
									{VATNumber} :{' '}
									{t('vatNumber', {
										lng: 'arabicValue'
									})}
								</Text>
							</View>
						</View>
					)}
					{showCRNumber && (
						<View style={[styles.tableRow, styles.textBlock]}>
							<View style={[{ width: '50%' }]}>
								<Text style={[styles.tableCell, { textAlign: 'left' }]}>
									{t('crNumber')} : {CRNumber}
								</Text>
							</View>
							<View style={[{ width: '50%' }]}>
								<Text style={[styles.tableCell, { textAlign: 'right' }]}>
									{CRNumber} :{' '}
									{t('crNumber', {
										lng: 'arabicValue'
									})}
								</Text>
							</View>
						</View>
					)}
					{showPaymentMethod && (
						<View style={[styles.tableRow, styles.textBlock]}>
							<View style={[{ width: '50%' }]}>
								<Text style={[styles.tableCell, { textAlign: 'left' }]}>
									{t('payment')} : {t(paymentMethod)}
								</Text>
							</View>
							<View style={[{ width: '50%' }]}>
								<Text style={[styles.tableCell, { textAlign: 'right' }]}>
									{paymentMethod} :{' '}
									{t(paymentMethod, {
										lng: 'arabicValue'
									})}
								</Text>
							</View>
						</View>
					)}
					{showFullName && (
						<View style={[styles.tableRow, styles.textBlock]}>
							<View style={[{ width: '50%' }]}>
								<Text style={[styles.tableCell, { textAlign: 'left' }]}>
									{t('fullName')} : {receiverName}
								</Text>
							</View>
							<View style={[{ width: '50%' }]}>
								<Text style={[styles.tableCell, { textAlign: 'right' }]}>
									{receiverName} :{' '}
									{t('fullName', {
										lng: 'arabicValue'
									})}
								</Text>
							</View>
						</View>
					)}
					{showPhoneNumber && (
						<View style={[styles.tableRow, styles.textBlock]}>
							<View style={[{ width: '50%' }]}>
								<Text style={[styles.tableCell, { textAlign: 'left' }]}>
									{t('telephone')} : {receiverTelephone}
								</Text>
							</View>
							<View style={[{ width: '50%' }]}>
								<Text style={[styles.tableCell, { textAlign: 'right' }]}>
									{receiverTelephone} :{' '}
									{t('telephone', {
										lng: 'arabicValue'
									})}
								</Text>
							</View>
						</View>
					)}
					{showAddress && (
						<View style={[styles.tableRow, styles.textBlock]}>
							<View style={[{ width: '50%' }]}>
								<Text style={[styles.tableCell, { textAlign: 'left' }]}>
									{t('address')} : {receiverAddress && <RTLTextForPDF text={receiverAddress} />}
								</Text>
							</View>
							<View style={[{ width: '50%' }]}>
								<Text style={[styles.tableCell, { textAlign: 'right' }]}>
									{receiverAddress && <RTLTextForPDF text={receiverAddress} />}:{' '}
									{t('address', {
										lng: 'arabicValue'
									})}
								</Text>
							</View>
						</View>
					)}
					{showCity && (
						<View style={[styles.tableRow, styles.textBlock]}>
							<View style={[{ width: '50%' }]}>
								<Text style={[styles.tableCell, { textAlign: 'left' }]}>
									{t('city')} : {receiverCity}
								</Text>
							</View>
							<View style={[{ width: '50%' }]}>
								<Text style={[styles.tableCell, { textAlign: 'right' }]}>
									{receiverCity} :{' '}
									{t('city', {
										lng: 'arabicValue'
									})}
								</Text>
							</View>
						</View>
					)}
					{showCountry && (
						<View style={[styles.tableRow, styles.textBlock]}>
							<View style={[{ width: '50%' }]}>
								<Text style={[styles.tableCell, { textAlign: 'left' }]}>
									{t('country')} : {t(`country.${receiverCountry}`)}
								</Text>
							</View>
							<View style={[{ width: '50%' }]}>
								<Text style={[styles.tableCell, { textAlign: 'right' }]}>
									{t(`country.${receiverCountry}`)} :{' '}
									{t('country', {
										lng: 'arabicValue'
									})}
								</Text>
							</View>
						</View>
					)}
					{showSenderName && (
						<View style={[styles.tableRow, styles.textBlock]}>
							<View style={[{ width: '50%' }]}>
								<Text style={[styles.tableCell, { textAlign: 'left' }]}>
									{t('senderName')} : {senderName}
								</Text>
							</View>
							<View style={[{ width: '50%' }]}>
								<Text style={[styles.tableCell, { textAlign: 'right' }]}>
									{senderName} :{' '}
									{t('senderName', {
										lng: 'arabicValue'
									})}
								</Text>
							</View>
						</View>
					)}
					{showSenderAddress && (
						<View style={[styles.tableRow, styles.textBlock]}>
							<View style={[{ width: '50%' }]}>
								<Text style={[styles.tableCell, { textAlign: 'left' }]}>
									{t('senderAddress')} :{' '}
									{(invoiceAddress || senderAddress) && (
										<RTLTextForPDF text={invoiceAddress ? invoiceAddress : senderAddress} />
									)}
								</Text>
							</View>
							<View style={[{ width: '50%' }]}>
								<Text style={[styles.tableCell, { textAlign: 'right' }]}>
									{(invoiceAddress || senderAddress) && (
										<RTLTextForPDF text={invoiceAddress ? invoiceAddress : senderAddress} />
									)}
									:{' '}
									{t('senderAddress', {
										lng: 'arabicValue'
									})}
								</Text>
							</View>
						</View>
					)}
					{showShippingDescription && (
						<View style={[styles.tableRow, styles.textBlock]}>
							<View style={[{ width: '50%' }]}>
								<Text style={[styles.tableCell, { textAlign: 'left' }]}>
									{t('shippingDescription')} : {shippingDescription}
								</Text>
							</View>
							<View style={[{ width: '50%' }]}>
								<Text style={[styles.tableCell, { textAlign: 'right' }]}>
									{shippingDescription} :{' '}
									{t('shippingDescription', {
										lng: 'arabicValue'
									})}
								</Text>
							</View>
						</View>
					)}
					{showOrderComment && (
						<View style={[styles.tableRow, styles.textBlock]}>
							<View style={[{ width: '50%' }]}>
								<Text style={[styles.tableCell, { textAlign: 'left' }]}>
									{t('orderComments')} : {orderComment}
								</Text>
							</View>
							<View style={[{ width: '50%' }]}>
								<Text style={[styles.tableCell, { textAlign: 'right' }]}>
									{orderComment} :{' '}
									{t('orderComments', {
										lng: 'arabicValue'
									})}
								</Text>
							</View>
						</View>
					)}
					{showCouponCode && (
						<View style={[styles.tableRow, styles.textBlock]}>
							<View style={[{ width: '50%' }]}>
								<Text style={[styles.tableCell, { textAlign: 'left' }]}>
									{t('couponCode')} : {couponCode ?? '-'}
								</Text>
							</View>
							<View style={[{ width: '50%' }]}>
								<Text style={[styles.tableCell, { textAlign: 'right' }]}>
									{couponCode ?? '-'} :{' '}
									{t('couponCode', {
										lng: 'arabicValue'
									})}
								</Text>
							</View>
						</View>
					)}
					{showRef2 && (
						<View style={[styles.tableRow, styles.textBlock]}>
							<View style={[{ width: '50%' }]}>
								<Text style={[styles.tableCell, { textAlign: 'left' }]}>
									{t('ref2')} : {ref2}
								</Text>
							</View>
							<View style={[{ width: '50%' }]}>
								<Text style={[styles.tableCell, { textAlign: 'right' }]}>
									{ref2} : {t('ref2', { lng: 'arabicValue' })}
								</Text>
							</View>
						</View>
					)}
				</View>

				{/* TABLE */}
				{/** second section */}
				{showOrderItems && (
					<PrintTable
						styles={{
							table: {
								padding: 2,
								borderBottomWidth: 0.5,
								borderTopWidth: 0,
								borderLeftWidth: 0,
								borderRightWidth: 0
							},
							thead: {
								theadRow: [
									styles.tableRow,
									{
										color: '#fff',
										backgroundColor: '#383838',
										display: 'flex',
										flexDirection: 'row',
										alignItems: 'center',
										overflow: 'hidden'
									}
								],
								theadCol: (_, i, arr) => ({
									borderRightWidth: i !== arr.length - 1 ? 0.5 : 0,
									paddingVertical: 10 * pageScale,
									paddingHorizontal: pageScale,
									display: 'flex',
									textAlign: 'center',
									alignItems: 'center'
								}),
								theadCell: {
									flexDirection: language === 'arabicValue' ? 'row-reverse' : 'row',
									textAlign: 'center'
								}
							},
							tbody: {
								tbodyRow: styles.tableRow,
								tbodyCol: { paddingVertical: 10 * pageScale, paddingHorizontal: pageScale },
								tbodyCell: {
									textAlign: 'center',
									flexDirection: language === 'arabicValue' ? 'row-reverse' : 'row'
								}
							},
							barcode: {
								width: '100%',
								paddingHorizontal: 2
							}
						}}
						itemConfig={itemConfig}
						items={items}
						columnSettings={columnSettings}
						wrap={true}
						language={language}
					/>
				)}
				{/* TABLE */}
				<View style={[styles.section]}>
					{showTotalQuantity && (
						<View style={[styles.tableRow, styles.textBlock]}>
							<View style={[{ width: '40%' }]}>
								<Text style={[styles.tableCell, { textAlign: 'left' }]}>{t('totalQuantity')}</Text>
							</View>
							<View style={[{ width: '40%' }]}>
								<Text style={[styles.tableCell, { textAlign: 'right' }]}>
									{t('totalQuantity', { lng: 'arabicValue' })}
								</Text>
							</View>
							<View style={[{ width: '20%' }]}>
								<Text style={[styles.tableCell, { textAlign: 'right' }]}>{totalQuantity}</Text>
							</View>
						</View>
					)}
					{showSubTotal && (
						<View style={[styles.tableRow, styles.textBlock]}>
							<View style={[{ width: '40%' }]}>
								<Text style={[styles.tableCell, { textAlign: 'left' }]}>{t('subtotal')}</Text>
							</View>
							<View style={[{ width: '40%' }]}>
								<Text style={[styles.tableCell, { textAlign: 'right' }]}>
									{t('subtotal', { lng: 'arabicValue' })}
								</Text>
							</View>
							<View style={[{ width: '20%' }]}>
								<Text style={[styles.tableCell, { textAlign: 'right' }]}>{subTotal}</Text>
							</View>
						</View>
					)}
					{showDiscount && (
						<View style={[styles.tableRow, styles.textBlock]}>
							<View style={[{ width: '40%' }]}>
								<Text style={[styles.tableCell, { textAlign: 'left' }]}>{t('discount_amount')}</Text>
							</View>
							<View style={[{ width: '40%' }]}>
								<Text style={[styles.tableCell, { textAlign: 'right' }]}>
									{t('discount_amount', { lng: 'arabicValue' })}
								</Text>
							</View>
							<View style={[{ width: '20%' }]}>
								<Text style={[styles.tableCell, { textAlign: 'right' }]}>
									{discountAmount}&nbsp;{currency}
								</Text>
							</View>
						</View>
					)}
					{showCodFee && (
						<View style={[styles.tableRow, styles.textBlock]}>
							<View style={[{ width: '40%' }]}>
								<Text style={[styles.tableCell, { textAlign: 'left' }]}>{t('codFee')}</Text>
							</View>
							<View style={[{ width: '40%' }]}>
								<Text style={[styles.tableCell, { textAlign: 'right' }]}>
									{t('codFee', { lng: 'arabicValue' })}
								</Text>
							</View>
							<View style={[{ width: '20%' }]}>
								<Text style={[styles.tableCell, { textAlign: 'right' }]}>
									{codFee}&nbsp;{currency}
								</Text>
							</View>
						</View>
					)}
					{showShipFee && (
						<View style={[styles.tableRow, styles.textBlock]}>
							<View style={[{ width: '40%' }]}>
								<Text style={[styles.tableCell, { textAlign: 'left' }]}>{t('shipping')}</Text>
							</View>
							<View style={[{ width: '40%' }]}>
								<Text style={[styles.tableCell, { textAlign: 'right' }]}>
									{t('shipping', { lng: 'arabicValue' })}
								</Text>
							</View>
							<View style={[{ width: '20%' }]}>
								<Text style={[styles.tableCell, { textAlign: 'right' }]}>
									{shippingAmount}&nbsp;{currency}
								</Text>
							</View>
						</View>
					)}
					{showShippingDiscount && (
						<View style={[styles.tableRow, styles.textBlock]}>
							<View style={[{ width: '40%' }]}>
								<Text style={[styles.tableCell, { textAlign: 'left' }]}>{t('shippingDiscount')}</Text>
							</View>
							<View style={[{ width: '40%' }]}>
								<Text style={[styles.tableCell, { textAlign: 'right' }]}>
									{t('shippingDiscount', { lng: 'arabicValue' })}
								</Text>
							</View>
							<View style={[{ width: '20%' }]}>
								<Text style={[styles.tableCell, { textAlign: 'right' }]}>
									{shippingDiscountAmount}&nbsp;{currency}
								</Text>
							</View>
						</View>
					)}
					{showVatAmount && !!taxAmount && (
						<View style={[styles.tableRow, styles.textBlock]}>
							<View style={[{ width: '40%' }]}>
								<Text style={[styles.tableCell, { textAlign: 'left' }]}>{t('vatAmount')}</Text>
							</View>
							<View style={[{ width: '40%' }]}>
								<Text style={[styles.tableCell, { textAlign: 'right' }]}>
									{t('vatAmount', { lng: 'arabicValue' })}
								</Text>
							</View>
							<View style={[{ width: '20%' }]}>
								<Text style={[styles.tableCell, { textAlign: 'right' }]}>
									{taxAmount}&nbsp;{currency}
								</Text>
							</View>
						</View>
					)}
					{showTotalDue && !!totalDue && (
						<View style={[styles.tableRow, styles.textBlock]}>
							<View style={[{ width: '40%' }]}>
								<Text style={[styles.tableCell, { textAlign: 'left' }]}>{t('totalDue')}</Text>
							</View>
							<View style={[{ width: '40%' }]}>
								<Text style={[styles.tableCell, { textAlign: 'right' }]}>
									{t('totalDue', { lng: 'arabicValue' })}
								</Text>
							</View>
							<View style={[{ width: '20%' }]}>
								<Text style={[styles.tableCell, { textAlign: 'right' }]}>
									{totalDue} {currency}
								</Text>
							</View>
						</View>
					)}
					{showGrandTotal && !!totalDue && (
						<View style={[styles.tableRow, styles.textBlock]}>
							<View style={[{ width: '40%' }]}>
								<Text style={[styles.tableCell, { textAlign: 'left' }]}>{t('grandTotal')}</Text>
							</View>
							<View style={[{ width: '40%' }]}>
								<Text style={[styles.tableCell, { textAlign: 'right' }]}>
									{t('grandTotal', { lng: 'arabicValue' })}
								</Text>
							</View>
							<View style={[{ width: '20%' }]}>
								<Text style={[styles.tableCell, { textAlign: 'right' }]}>
									{grandTotal}&nbsp;{currency}
								</Text>
							</View>
						</View>
					)}
				</View>
			</View>
			<View
				style={[
					styles.tableRow,
					{ justifyContent: 'center', marginTop: pageSize !== 'A7' || pageSize !== 'A6' ? 15 : 0 }
				]}>
				{showQrCode && !!qrCode && (
					<View style={[styles.qrCode]}>
						<Image src={qrCode} />
					</View>
				)}
			</View>
		</>
	);
};

export default Template5;
